import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bell, Check, Eye, List, RefreshCw, Trash2 } from "lucide-react";
import { useProcessMonitoring } from "../context/ProcessMonitoringContext";
import ProcessNumber from "../components/common/ProcessNumber";

function formatDateBR(value) {
  if (!value) return "";
  try {
    const d = value instanceof Date ? value : new Date(value);
    return d.toLocaleDateString("pt-BR");
  } catch {
    return "";
  }
}

export default function MeusMonitoramentos() {
  const location = useLocation();
  const {
    monitorings,
    unreadCount,
    notifications,
    refreshAll,
    fetchNotifications,
    toggleMonitoring,
    markNotificationRead,
    markAllNotificationsRead,
  } = useProcessMonitoring();

  const [tab, setTab] = useState(() => location.state?.tab || "monitorings"); // monitorings | notifications
  const [includeRead, setIncludeRead] = useState(true);
  const [loading, setLoading] = useState(false);

  const sortedMonitorings = useMemo(() => {
    return [...(monitorings || [])].sort((a, b) => {
      const da = new Date(a.createdAt).getTime();
      const db = new Date(b.createdAt).getTime();
      return db - da;
    });
  }, [monitorings]);

  useEffect(() => {
    // sempre que entrar na tela, atualiza
    (async () => {
      setLoading(true);
      try {
        await refreshAll();
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshAll]);

  useEffect(() => {
    if (tab !== "notifications") return;
    (async () => {
      setLoading(true);
      try {
        await fetchNotifications({ includeRead });
      } finally {
        setLoading(false);
      }
    })();
  }, [tab, includeRead, fetchNotifications]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await refreshAll();
      if (tab === "notifications") {
        await fetchNotifications({ includeRead });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMonitoring = async (numberProcess) => {
    setLoading(true);
    try {
      await toggleMonitoring(numberProcess);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    setLoading(true);
    try {
      await markNotificationRead(notificationId);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await markAllNotificationsRead();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Meus monitoramentos</h1>
          <p className="text-sm text-gray-600">
            Favoritos por processo e notificações quando houver novas publicações.
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border border-gray-300 bg-gray-50 text-gray-800 shadow-sm hover:bg-gray-100 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </div>

      <div className="px-6 pt-4">
        <div className="inline-flex rounded-xl border border-gray-300 bg-gray-50 overflow-hidden shadow-sm">
          <button
            onClick={() => setTab("monitorings")}
            className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-2 ${
              tab === "monitorings"
                ? "bg-gray-900 text-white"
                : "bg-gray-50 text-gray-800 hover:bg-gray-100"
            }`}
          >
            <List className="w-4 h-4" />
            Monitoramentos ({monitorings?.length || 0})
          </button>

          <button
            onClick={() => setTab("notifications")}
            className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-2 ${
              tab === "notifications"
                ? "bg-gray-900 text-white"
                : "bg-gray-50 text-gray-800 hover:bg-gray-100"
            }`}
          >
            <Bell className="w-4 h-4" />
            Notificações {unreadCount > 0 ? `(${unreadCount})` : ""}
          </button>
        </div>
      </div>

      {tab === "monitorings" ? (
        <div className="p-6">
          {sortedMonitorings.length === 0 ? (
            <div className="text-sm text-gray-600">
              Você ainda não está monitorando nenhum processo. Clique na ⭐ ao lado do CNJ em Publicações ou Processos.
            </div>
          ) : (
            <div className="space-y-3">
              {sortedMonitorings.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between p-4 rounded-2xl border border-gray-200 hover:bg-gray-50"
                >
                  <div className="flex flex-col">
                    <div className="flex items-center gap-3">
                      <ProcessNumber processNumber={m.numberProcess} copyable />
                      <span className="text-xs text-gray-500">adicionado em {formatDateBR(m.createdAt)}</span>
                    </div>
                    <span className="text-xs text-gray-500">ID: {m.id}</span>
                  </div>

                  <button
                    onClick={() => handleRemoveMonitoring(m.numberProcess)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border border-red-200 bg-red-50 text-red-700 shadow-sm hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                    title="Remover do monitoramento"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remover
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeRead}
                onChange={(e) => setIncludeRead(e.target.checked)}
              />
              Mostrar lidas
            </label>

            <button
              onClick={handleMarkAllRead}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border border-gray-300 bg-gray-50 text-gray-800 shadow-sm hover:bg-gray-100 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              Marcar todas como lidas
            </button>
          </div>

          {notifications.length === 0 ? (
            <div className="text-sm text-gray-600">Nenhuma notificação encontrada.</div>
          ) : (
            <div className="space-y-3">
              {notifications.map((n) => {
                const isDje = !!n.publicationDjeId || !!n.publicationDje;
                const title = isDje
                  ? `DJE • ${n.publicationDje?.siglaTribunal || "Tribunal"}`
                  : `2ADV • ${n.publication?.tribunal || "Tribunal"}`;

                const date = isDje
                  ? formatDateBR(n.publicationDje?.dataDisponibilizacao)
                  : formatDateBR(n.publication?.datePublication);

                const summary = isDje ? n.publicationDje?.resumo || "" : n.publication?.summary || "";

                return (
                  <div
                    key={n.id}
                    className={`p-4 rounded-2xl border ${
                      n.read ? "border-gray-200 bg-white" : "border-blue-200 bg-blue-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-700">{title}</span>
                          <span className="text-xs text-gray-500">{date}</span>
                          {!n.read && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white">
                              nova
                            </span>
                          )}
                        </div>

                        <div className="mt-1">
                          <ProcessNumber processNumber={n.numberProcess} copyable />
                        </div>

                        {summary && (
                          <p className="mt-2 text-sm text-gray-700 line-clamp-3">{summary}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!n.read && (
                          <button
                            onClick={() => handleMarkRead(n.id)}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-800 shadow-sm hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Marcar como lida"
                          >
                            <Eye className="w-4 h-4" />
                            Lida
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}